import { lazy, memo, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { SearchIcon as Search, ShoppingBagIcon as ShoppingBag, HeartIcon as Heart, MenuIcon as Menu, XIcon as X, UserIcon as User, ChevronDownIcon as ChevronDown } from "./icons";
import { useQueryClient } from "@tanstack/react-query";
import { prefetchProductsForPath } from "@/lib/prefetchProducts";
import { useMenuCategories } from "@/hooks/useCategories";
import { useCart } from "@/contexts/CartContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { useSiteContent } from "@/hooks/useSiteContent";
import { navigateToStorefrontHome, scrollToStorefrontTarget, buildStorefrontHomePath } from "@/lib/storefrontNavigation";

const SearchModal = lazy(() => import("./SearchModal"));
const getSupabase = () => import("@/integrations/supabase/client").then((m) => m.supabase);

const Header = memo(() => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [catDropdownOpen, setCatDropdownOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [scrolled, setScrolled] = useState(false);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const mobileToggleRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { openCart, closeCart, totalItems } = useCart();
  const { t } = useLanguage();
  const { content: siteSettings, loading: settingsLoading } = useSiteContent();
  const shouldLoadMenuCategories = catDropdownOpen || mobileOpen;
  const { data: categories = [] } = useMenuCategories(shouldLoadMenuCategories);

  const brandName = siteSettings.brand_name || "";
  const logoUrl = siteSettings.logo_url?.trim() || "";

  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        setScrolled(window.scrollY > 8);
        ticking = false;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    let cleanupAuth = () => {};
    let initialized = false;
    const setupAuth = async () => {
      if (initialized) return;
      initialized = true;
      const supabase = await getSupabase();
      const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => setUser(session?.user ?? null));
      supabase.auth.getSession().then(({ data: { session } }) => setUser(session?.user ?? null));
      cleanupAuth = () => subscription.unsubscribe();
    };
    const events: Array<keyof WindowEventMap> = ["pointerdown", "touchstart", "keydown", "scroll"];
    const onInteract = () => {
      events.forEach((eventName) => window.removeEventListener(eventName, onInteract));
      setupAuth();
    };
    events.forEach((eventName) => window.addEventListener(eventName, onInteract, { once: true, passive: true }));
    return () => {
      events.forEach((eventName) => window.removeEventListener(eventName, onInteract));
      cleanupAuth();
    };
  }, []);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (mobileOpen && mobileMenuRef.current && !mobileMenuRef.current.contains(e.target as Node) && mobileToggleRef.current && !mobileToggleRef.current.contains(e.target as Node)) {
        setMobileOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [mobileOpen]);

  const showReviews = siteSettings.show_reviews_in_header === "true";
  const navLinks = useMemo(() => [
    { label: t("nav_home"), target: "home", key: "home" },
    { label: t("nav_shop"), target: "products", isPage: true, key: "shop" },
    { label: t("nav_categories"), target: "categories", isDropdown: true, key: "categories" },
    { label: t("nav_best_selling"), target: "best-selling", isPage: true, key: "best-selling" },
    { label: t("nav_new_arrival", "New Arrival"), target: "new-arrivals", isPage: true, key: "new-arrivals" },
    { label: t("nav_baby_care_tips", "Baby Care Tips"), target: "baby-care-tips", isPage: true, key: "baby-care-tips" },
    ...(showReviews ? [{ label: t("nav_reviews", "Reviews"), target: "reviews", isPage: true, key: "reviews" }] : []),
    { label: t("nav_contact"), target: "contact", key: "contact" },
  ], [showReviews, t]);

  const handlePrefetch = useCallback((target: string) => prefetchProductsForPath(`/${target}`, queryClient), [queryClient]);

  const scrollToSection = useCallback((target: string) => {
    setMobileOpen(false);
    closeCart();
    if (target === "home") {
      if (location.pathname === "/") window.scrollTo({ top: 0, behavior: "smooth" });
      else navigateToStorefrontHome(navigate, location.search, { scrollToTop: true });
      return;
    }
    if (target === "contact") {
      if (location.pathname !== "/") navigateToStorefrontHome(navigate, location.search, { scrollTarget: "contact" });
      else document.getElementById("contact")?.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    if (location.pathname !== "/") navigateToStorefrontHome(navigate, location.search, { scrollTarget: target });
    else scrollToStorefrontTarget(target);
  }, [closeCart, location.pathname, location.search, navigate]);

  const handleCategoryClick = useCallback((slug: string) => {
    setCatDropdownOpen(false);
    setMobileCatOpen(false);
    setMobileOpen(false);
    navigate(`/category/${slug}`);
  }, [navigate]);

  const navButtonClass = "relative px-4 py-2 font-heading text-[12px] font-semibold uppercase tracking-[0.22em] text-foreground/80 transition-colors duration-300 hover:text-primary after:absolute after:left-1/2 after:-bottom-1 after:h-[2px] after:w-0 after:-translate-x-1/2 after:bg-primary after:transition-all after:duration-300 hover:after:w-[70%]";

  return (
    <>
      <header className={`sticky top-0 z-50 border-b border-border !bg-[hsl(var(--storefront-header))] transition-shadow ${scrolled ? "shadow-sm" : ""}`}>
        <div className="!bg-[hsl(var(--storefront-header))] text-foreground">
          <div className="mx-auto max-w-[1800px] px-3 md:px-6">
            <div className="relative flex h-[64px] items-center justify-between md:h-[84px]">
              {/* Left cluster */}
              <div className="flex flex-1 items-center justify-start gap-2 md:gap-2">
                <button ref={mobileToggleRef} aria-label="Menu" className="lg:hidden flex h-10 w-10 items-center justify-center rounded-full border border-foreground/10 bg-background/60 text-foreground/80 transition-colors duration-300 hover:border-accent/40 hover:text-foreground active:scale-95" onClick={() => setMobileOpen(true)}>
                  <Menu size={17} strokeWidth={1.2} />
                </button>
                <button aria-label="Search" onClick={() => setSearchOpen(true)} className="flex h-10 w-10 items-center justify-center rounded-full border border-foreground/10 bg-background/60 text-foreground/80 transition-colors duration-300 hover:border-accent/40 hover:text-foreground active:scale-95 lg:hidden">
                  <Search size={16} strokeWidth={1.2} />
                </button>
                <button aria-label="Search" onClick={() => setSearchOpen(true)} className="hidden h-10 items-center gap-2 rounded-sm bg-background/95 px-3 text-left text-xs text-muted-foreground transition-colors hover:bg-background lg:flex lg:w-[260px] xl:w-[320px]">
                  <Search size={16} strokeWidth={1.6} />
                  <span className="truncate">Search for an item, colour or season...</span>
                </button>
              </div>

              {/* Centered logo */}
              <a
                href={buildStorefrontHomePath(location.search)}
                onClick={(e) => { e.preventDefault(); scrollToSection("home"); }}
                aria-label={brandName}
                className="absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center justify-center"
              >
                {logoUrl ? (
                  <img
                    src={logoUrl}
                    alt={brandName}
                    width={180}
                    height={64}
                    className="h-10 w-auto object-contain md:h-14"
                  />
                ) : (
                  <span className="font-body text-xl font-semibold uppercase tracking-[0.28em] text-foreground md:text-2xl">{brandName}</span>
                )}
              </a>

              {/* Right cluster */}
              <div className="flex flex-1 items-center justify-end gap-2 md:gap-2">
                <button aria-label="Wishlist" onClick={() => navigate("/wishlist")} className="hidden h-10 w-10 items-center justify-center text-foreground/85 transition-colors hover:text-foreground sm:flex">
                  <Heart size={19} strokeWidth={1.4} />
                </button>
                <button aria-label="Cart" onClick={openCart} className="relative flex h-10 w-10 items-center justify-center rounded-full border border-foreground/10 bg-background/60 text-foreground/80 transition-colors duration-300 hover:border-accent/40 hover:text-foreground active:scale-95 md:border-0 md:bg-transparent">
                  <ShoppingBag size={16} strokeWidth={1.2} className="md:hidden" />
                  <ShoppingBag size={19} strokeWidth={1.4} className="hidden md:block" />
                  {totalItems > 0 && <span className="absolute -right-0.5 -top-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-accent px-1 text-[9px] font-semibold text-accent-foreground ring-2 ring-[hsl(var(--storefront-header))]">{totalItems}</span>}
                </button>
                <button aria-label={user ? "Profile" : "Login"} onClick={() => navigate(user ? "/profile" : "/auth")} className="flex h-10 w-10 items-center justify-center rounded-full border border-foreground/10 bg-background/60 text-foreground/80 transition-colors duration-300 hover:border-accent/40 hover:text-foreground active:scale-95 md:hidden">
                  <User size={16} strokeWidth={1.2} />
                </button>
                <button aria-label={user ? "Profile" : "Login"} onClick={() => navigate(user ? "/profile" : "/auth")} className="hidden h-10 items-center gap-2 px-2 text-xs font-semibold uppercase tracking-[0.14em] text-foreground/90 transition-colors hover:text-foreground md:flex">
                  <User size={19} strokeWidth={1.5} /> <span className="hidden xl:inline">Sign In</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-background text-foreground border-b border-border">
          <div className="mx-auto max-w-[1800px] px-3 md:px-6">
          <nav className="hidden h-14 items-center justify-center gap-2 lg:flex">
            {navLinks.map((link) => (link as any).isDropdown ? (
              <div key={link.key} className="relative" onMouseEnter={() => setCatDropdownOpen(true)} onMouseLeave={() => setCatDropdownOpen(false)}>
                <button onClick={() => setCatDropdownOpen((v) => !v)} className={`${navButtonClass} flex items-center gap-1`}>
                  {link.label}<ChevronDown size={12} className={catDropdownOpen ? "rotate-180 transition-transform" : "transition-transform"} />
                </button>
                {catDropdownOpen && (
                  <div className="absolute left-1/2 top-full z-[60] w-72 -translate-x-1/2 pt-3">
                    <div className="border border-border bg-card p-3 shadow-xl">
                    {categories.length === 0 ? <p className="px-3 py-2 text-xs text-muted-foreground">No categories</p> : categories.map((cat) => (
                      <button key={cat.id} onClick={() => handleCategoryClick(cat.slug)} className="flex w-full items-center gap-3 px-3 py-2.5 text-left text-xs uppercase tracking-[0.14em] text-foreground/70 transition-colors hover:bg-secondary hover:text-foreground">
                        {cat.image && <img src={cat.image} alt={cat.name} width={34} height={34} className="h-8 w-8 object-cover" loading="lazy" />}
                        {cat.name}
                      </button>
                    ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <button key={link.key} onMouseEnter={() => (link as any).isPage && handlePrefetch(link.target)} onClick={() => (link as any).isPage ? navigate(`/${link.target}`) : scrollToSection(link.target)} className={navButtonClass}>
                {link.label}
              </button>
            ))}
          </nav>
          </div>
        </div>
      </header>

      {mobileOpen && <div className="fixed inset-0 z-[55] bg-foreground/40 lg:hidden" onClick={() => setMobileOpen(false)} />}
      <div ref={mobileMenuRef} className={`fixed inset-y-0 left-0 z-[56] w-[86vw] max-w-sm transform bg-card shadow-2xl transition-transform duration-300 lg:hidden ${mobileOpen ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="flex h-full flex-col">
          <div className="flex items-center justify-between border-b border-border px-5 py-5">
            <button
              onClick={() => { setMobileOpen(false); navigate("/"); window.scrollTo({ top: 0, behavior: "smooth" }); }}
              className="flex items-center"
              aria-label="Go to home"
            >
              {logoUrl ? <img src={logoUrl} alt={brandName} className="h-10 w-auto" /> : <span className="font-heading text-xl">{brandName}</span>}
            </button>
            <button onClick={() => setMobileOpen(false)} className="p-2 text-foreground"><X size={22} strokeWidth={1.4} /></button>
          </div>
          <div className="flex-1 overflow-y-auto px-3 py-4">
            <ul className="space-y-0.5">
              {navLinks.map((link, idx) => (
                <li key={link.key}>
                  {idx > 0 && <div className="mx-4 h-px bg-border/50" />}
                  {(link as any).isDropdown ? (
                    <>
                      <button
                        onClick={() => setMobileCatOpen((v) => !v)}
                        className="flex w-full items-center justify-between rounded-xl px-4 py-3.5 text-[13px] font-bold uppercase tracking-[0.14em] text-foreground transition-all hover:bg-primary/10 hover:text-primary"
                      >
                        {link.label}
                        <ChevronDown size={14} className={`text-muted-foreground transition-transform ${mobileCatOpen ? "rotate-180" : ""}`} />
                      </button>
                      {mobileCatOpen && (
                        <div className="mt-1 space-y-0.5 pl-4">
                          {categories.map((cat) => (
                            <button
                              key={cat.id}
                              onClick={() => handleCategoryClick(cat.slug)}
                              className="block w-full rounded-lg px-4 py-2.5 text-left text-[11px] uppercase tracking-[0.14em] text-muted-foreground transition-colors hover:bg-primary/5 hover:text-primary"
                            >
                              {cat.name}
                            </button>
                          ))}
                          <button
                            onClick={() => { setMobileOpen(false); navigate("/products"); }}
                            className="block w-full rounded-lg px-4 py-2.5 text-left text-[11px] uppercase tracking-[0.14em] text-muted-foreground transition-colors hover:bg-primary/5 hover:text-primary"
                          >
                            {t("all_products", "All Products")}
                          </button>
                        </div>
                      )}
                    </>
                  ) : (
                    <button
                      onClick={() => { setMobileOpen(false); (link as any).isPage ? navigate(`/${link.target}`) : scrollToSection(link.target); }}
                      className="flex w-full items-center rounded-xl px-4 py-3.5 text-left text-[13px] font-bold uppercase tracking-[0.14em] text-foreground transition-all hover:bg-primary/10 hover:text-primary"
                    >
                      {link.label}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>
          <div className="grid grid-cols-3 divide-x divide-border/60 border-t border-border bg-card pb-[calc(env(safe-area-inset-bottom)+5rem)] lg:pb-0">
            <button onClick={() => setSearchOpen(true)} className="group flex flex-col items-center justify-center gap-1.5 py-4 transition-colors hover:bg-primary/5">
              <Search size={19} strokeWidth={1.6} className="text-muted-foreground transition-colors group-hover:text-primary" />
              <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground transition-colors group-hover:text-primary">Search</span>
            </button>
            <button onClick={() => navigate("/wishlist")} className="group flex flex-col items-center justify-center gap-1.5 py-4 transition-colors hover:bg-primary/5">
              <Heart size={19} strokeWidth={1.6} className="text-muted-foreground transition-colors group-hover:text-primary" />
              <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground transition-colors group-hover:text-primary">Wishlist</span>
            </button>
            <button onClick={openCart} className="group flex flex-col items-center justify-center gap-1.5 py-4 transition-colors hover:bg-primary/5">
              <ShoppingBag size={19} strokeWidth={1.6} className="text-muted-foreground transition-colors group-hover:text-primary" />
              <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground transition-colors group-hover:text-primary">Cart</span>
            </button>
          </div>
        </div>
      </div>

      {searchOpen && <Suspense fallback={null}><SearchModal open={searchOpen} onClose={() => setSearchOpen(false)} /></Suspense>}
    </>
  );
});

Header.displayName = "Header";
export default Header;
